import type { GranularityType, MetricType } from "@/hooks/useEvolutionChartData";
import type { DashboardByMaterialPerDay } from "@/integrations/agreements/client";

export interface AggregatedChartPoint {
  period: string;
  [materialId: string]: number | string;
}

export interface AggregatedChartData {
  points: AggregatedChartPoint[];
  /** Nome de exibição por material_id — usado por legenda e tooltip. */
  namesById: Record<string, string>;
}

function getPeriodKey(
  row: DashboardByMaterialPerDay,
  granularity: GranularityType,
): string {
  if (granularity === "day") return row.date;
  if (granularity === "month") return String(row.month);
  // granularity === "week": usa a data de início da semana
  return String(row.week);
}

/**
 * Agrupa por material_id, não pelo nome — o nome do material pode ser
 * renomeado no cadastro (registry_materials), o id não. `namesById` guarda
 * o nome só pra exibição (legenda/tooltip).
 */
export function aggregateByMaterialPerDay(
  rows: DashboardByMaterialPerDay[],
  granularity: GranularityType,
  metric: MetricType,
): AggregatedChartData {
  const keyField =
    metric === "weight"
      ? "total_weight"
      : metric === "volume"
        ? "total_volume"
        : "total_units";
  const byPeriod: Record<string, Record<string, number>> = {};
  const namesById: Record<string, string> = {};
  for (const row of rows) {
    const period = getPeriodKey(row, granularity);
    const materialKey = String(row.material_id);
    namesById[materialKey] = row.material || "Outros";
    if (!byPeriod[period]) byPeriod[period] = {};
    byPeriod[period][materialKey] =
      (byPeriod[period][materialKey] || 0) + (row[keyField] ?? 0);
  }
  const points = Object.entries(byPeriod)
    .map(([period, materials]) => ({ period, ...materials }))
    .sort((a, b) => a.period.localeCompare(b.period));
  return { points, namesById };
}

/**
 * Ordena material_ids pelo total somado em todos os períodos, do maior pro
 * menor. É a ordem da LEGENDA (Figma node 233:2525) — para a ordem de
 * empilhamento use `toStackOrder`, que é a inversa.
 */
export function sortMaterialIdsByTotalValueDesc(
  points: AggregatedChartPoint[],
  materialIds: string[],
): string[] {
  const totals = new Map<string, number>(materialIds.map((id) => [id, 0]));
  for (const point of points) {
    for (const id of materialIds) {
      totals.set(id, (totals.get(id) ?? 0) + (Number(point[id]) || 0));
    }
  }
  return [...materialIds].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
}

/**
 * Converte a ordem da legenda (maior primeiro) na ordem de empilhamento do
 * Recharts, que declara o primeiro `<Bar>` na BASE — ou seja, a inversa:
 * menor na base, maior no topo, como no Figma (node 233:2371).
 *
 * Além da fidelidade visual, é o que faz o canto arredondado aparecer:
 * `roundedBarPath` limita o raio a metade da altura do segmento, então um
 * material minúsculo no topo produzia um topo praticamente reto.
 */
export function toStackOrder(legendOrder: string[]): string[] {
  return [...legendOrder].reverse();
}
